import React, { useState } from "react";
import { X, Search, Loader2, CheckCircle2, AlertCircle, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Markdown from "../lib/Markdown";

export default function DeepResearch({ open, onClose, aiConfigured }) {
  const [query, setQuery] = useState("");
  const [running, setRunning] = useState(false);
  const [plan, setPlan] = useState(null);

  if (!open) return null;

  const run = async () => {
    if (!query.trim()) return;
    if (!aiConfigured) { toast.error("Deep Research requires an AI provider."); return; }
    setRunning(true); setPlan(null);
    try {
      const r = await api.deepResearch({ query: query.trim() });
      const id = r.id || r.plan?.id;
      let cur = r.plan; setPlan(cur);
      for (let i = 0; i < 80 && cur?.status === "running"; i++) {
        await new Promise((res) => setTimeout(res, 2500));
        try { const s = await api.getResearch(id); cur = s.plan; setPlan(cur); } catch (e) { /* keep polling */ }
      }
      if (cur?.status === "error") toast.error("Some live sources couldn't be reached — showing partial results.");
      else if (cur?.status === "running") toast.message("Research is taking longer than expected — it will finish shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Research failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="deep-research-modal">
      <div className="bg-[#0B0F17] border border-white/10 rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400"><Search size={18} /></div>
            <div>
              <div className="font-head font-bold text-white">Deep Research</div>
              <div className="text-[11px] text-slate-500">Multi-step, source-grounded synthesis</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {!aiConfigured && (
            <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
              <AlertCircle size={15} /> AI provider not configured — Deep Research is unavailable.
            </div>
          )}
          <div className="flex gap-2">
            <input data-testid="research-query-input" value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Research topic, e.g. EV battery market in India 2026"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-200" />
            <button data-testid="run-research-button" onClick={run} disabled={running || !aiConfigured}
              className="px-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40">
              {running ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Research
            </button>
          </div>

          {running && (
            <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Planning, gathering sources and synthesizing… this can take up to a minute.</div>
          )}

          {plan && (
            <div className="space-y-4">
              <div className="space-y-2">
                {plan.steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    {s.status === "error" ? <AlertCircle size={16} className="text-red-400 mt-0.5" />
                      : s.status === "pending" ? <Loader2 size={16} className="text-blue-400 mt-0.5 animate-spin" />
                      : <CheckCircle2 size={16} className="text-emerald-400 mt-0.5" />}
                    <span className="text-slate-300">{s.title}</span>
                  </div>
                ))}
              </div>
              {plan.finalDossier && (
                <div className="bg-[#111827] border border-white/10 rounded-xl p-5">
                  <Markdown>{plan.finalDossier}</Markdown>
                </div>
              )}
              {plan.sources?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 mb-2"><Globe size={12} /> Sources ({plan.sources.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {plan.sources.map((s, i) => (
                      <a key={i} href={s.uri} target="_blank" rel="noreferrer" className="flex items-center gap-1 max-w-[240px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-slate-300 hover:text-emerald-300">
                        <span className="truncate">{s.title}</span><ExternalLink size={11} className="shrink-0 opacity-60" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
